import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

export const createTypesensePartSearchAdapter = (mobileConfigs: any) => {
  try {
    if (!mobileConfigs?.typesense_host || !mobileConfigs?.typesense_api_key) {
      console.log("Typesense config missing!");
      return null;
    }
    return new TypesenseInstantSearchAdapter({
      server: {
        nodes: [
          {
            host: mobileConfigs.typesense_host,
            port: 443,
            protocol: "https",
          },
        ],
        apiKey: mobileConfigs.typesense_api_key,
      },
      additionalSearchParameters: {
        // Earlier `query_by` fields get higher implicit weight; prioritize part number over name at rank time.
        query_by: "partNumber,partName",
        // Per-field infix matches `query_by` order (typed as OperationMode[]).
        infix: ["fallback", "off"],
      },
    });
  } catch (error) {
    console.error("Failed to create Typesense adapter:", error);
    return null;
  }
};

export const mapTypesenseHitToBackendFormat = (part: any) => {
  try {
    console.log(part);
    const src = part?.document ? { ...part, ...part.document } : part;
    return {
      ...src,
      _id: src._id || src.partId || part._id,
    };
  } catch (error) {
    console.error("Failed to map Typesense hit:", error);
    return part;
  }
};

function partNumberAndName(part: any): { pn: string; name: string } {
  const pn = String(
    part?.partNumber ?? part?.["Part Number"] ?? "",
  ).trim();
  const name = String(
    part?.partName ?? part?.name ?? part?.["Part Name"] ?? "",
  ).trim();
  return { pn, name };
}

/**
 * Lower rank = show first. Exact / prefix / infix part-number matches before name-only or loose digit matches.
 */
function getPartSearchRelevanceRank(part: any, queryRaw: string): number {
  const q = queryRaw.trim().toLowerCase();
  if (!q) return 99;
  const { pn, name } = partNumberAndName(part);
  const pnL = pn.toLowerCase();
  const nameL = name.toLowerCase();
  if (pnL === q) return 0;
  if (pnL.startsWith(q)) return 1;
  if (pnL.includes(q)) return 2;
  if (nameL.includes(q)) return 3;
  const qAlnum = q.replace(/[^a-z0-9]/g, "");
  if (qAlnum.length >= 2) {
    const pnAl = pnL.replace(/[^a-z0-9]/g, "");
    const nameAl = nameL.replace(/[^a-z0-9]/g, "");
    if (pnAl.includes(qAlnum)) return 4;
    if (nameAl.includes(qAlnum)) return 5;
  }
  return 6;
}

/**
 * Dedupe by id or part number.
 * Pass backend (Mongo) hits as `primaryParts` so they keep lower pre-sort indices:
 * `sortPartsBySearchRelevance` breaks rank ties by originalIndex, and we slice(limit)
 * after sort — otherwise many Typesense prefix ties can push strong DB matches off page 1.
 */
export function mergeUniquePartResults(
  primaryParts: any[],
  secondaryParts: any[],
): any[] {
  const keyOf = (p: any): string => {
    const id = p?._id ?? p?.partId;
    if (id != null && String(id).length > 0) return `id:${String(id)}`;
    const pn = String(
      p?.partNumber ?? p?.["Part Number"] ?? "",
    )
      .trim()
      .toLowerCase();
    return pn ? `pn:${pn}` : "";
  };
  const out: any[] = [];
  const seen = new Set<string>();
  for (const p of primaryParts) {
    const k = keyOf(p);
    if (!k) {
      out.push(p);
      continue;
    }
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  for (const p of secondaryParts) {
    const k = keyOf(p);
    if (!k) {
      out.push(p);
      continue;
    }
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

/** One page of part search for infinite queries: `hasMore` avoids relying on `items.length === limit` when merge/dedupe shrinks page 1. */
export type PartSearchPageResult = {
  items: any[];
  hasMore: boolean;
};

/** Re-order hits so part-number-aligned matches rank above fuzzy / name-heavy matches (per page). */
export function sortPartsBySearchRelevance(
  parts: any[],
  query: string,
): any[] {
  if (!parts?.length || !query.trim()) return parts ?? [];
  const q = query.trim();
  return [...parts]
    .map((p, originalIndex) => ({
      p,
      originalIndex,
      rank: getPartSearchRelevanceRank(p, q),
    }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.originalIndex - b.originalIndex;
    })
    .map((x) => x.p);
}

export const searchPartsInTypesense = async (
  typesenseAdapter: any,
  partNumber: string,
  page: number = 1,
  limit: number = 20,
): Promise<{ hits: any[]; hasMore: boolean }> => {
  try {
    const results = await typesenseAdapter.searchClient.search([
      {
        indexName: "partmasters",
        params: {
          query: partNumber.trim(),
          hitsPerPage: limit,
          page: page - 1,
        },
      },
    ] as any);
    console.log("results", results);

    const r = (results as any).results?.[0];
    const hits = r?.hits || [];
    const nbPages =
      typeof r?.nbPages === "number"
        ? r.nbPages
        : typeof r?.nbHits === "number" && limit > 0
          ? Math.ceil(r.nbHits / limit)
          : 0;
    const pageIndex = typeof r?.page === "number" ? r.page : page - 1;
    const hasMore = nbPages > 0 && pageIndex + 1 < nbPages;
    return { hits, hasMore };
  } catch (error) {
    console.error("Typesense search failed:", error);
    return { hits: [], hasMore: false };
  }
};

export const performPartSearchStrategy = async (
  partNumber: string,
  context: {
    typesenseAdapter: any;
    backendSearch: (partNumber: string) => Promise<any>;
    page?: number;
    limit?: number;
  },
): Promise<PartSearchPageResult> => {
  const trimmedPartNumber = partNumber.trim();
  if (trimmedPartNumber === "")
    return { items: [], hasMore: false };
  const page = context.page || 1;
  const limit = context.limit || 20;

  // Strategy 1: Typesense
  try {
    console.log(
      "Strategy 1: context.typesenseAdapter exists?",
      !!context.typesenseAdapter,
    );
    if (context.typesenseAdapter) {
      console.log("Calling searchPartsInTypesense for:", trimmedPartNumber);
      const { hits, hasMore: typesenseHasMore } = await searchPartsInTypesense(
        context.typesenseAdapter,
        trimmedPartNumber,
        page,
        limit,
      );
      if (hits && hits.length > 0) {
        let mapped = hits.map((hit: any) =>
          mapTypesenseHitToBackendFormat(hit),
        );
        if (page === 1) {
          try {
            const raw = await context.backendSearch(trimmedPartNumber);
            const backendRows = Array.isArray(raw) ? raw : [];
            mapped = mergeUniquePartResults(backendRows, mapped);
          } catch (mergeErr) {
            console.error("Part search backend merge failed:", mergeErr);
          }
        }
        const sorted = sortPartsBySearchRelevance(
          mapped,
          trimmedPartNumber,
        );
        const items = sorted.slice(0, limit);
        return { items, hasMore: typesenseHasMore };
      }
    }
  } catch (error) {
    console.error("Strategy 1 (Typesense) failed:", error);
  }

  // Strategy 2: Backend Fallback (Only on page 1)
  if (page === 1) {
    try {
      const raw = await context.backendSearch(trimmedPartNumber);
      const rows = Array.isArray(raw) ? raw : [];
      const items = sortPartsBySearchRelevance(rows, trimmedPartNumber).slice(
        0,
        limit,
      );
      return { items, hasMore: false };
    } catch (error) {
      console.error("Strategy 2 (Backend) failed:", error);
      return { items: [], hasMore: false };
    }
  }

  return { items: [], hasMore: false };
};
